/**
 * Reactive form of the build-ahead feature-flag gate (see `config/featureFlags`).
 *
 * The gate predicates resolve at call time, so a page reload always reflects the current flags.
 * This hook adds LIVE reactivity: it subscribes to the runtime `show_in_progress` reveal flag
 * (see {@link useShowInProgress}) so the host component re-renders the instant a gated feature is
 * unlocked/locked, no reload needed. Use it in any component or hook that renders gated feature
 * UI; the bare predicates from the config stay for non-React code.
 *
 * Returns the same predicates plus `revealed` — pass `revealed` into `useMemo` / `useEffect`
 * dependency arrays so memoised/derived state recomputes when the flag flips (the predicate
 * references themselves are stable and would not trip a dependency check on their own).
 */
import React from 'react';

import { isFeatureEnabled, isFeatureLocked, featureRevealSnapshot } from '../config/featureFlags';
import { subscribeReveal } from './useShowInProgress';

export type FeatureGate = {
  /** Whether the runtime `show_in_progress` flag is currently revealing gated features. */
  revealed: boolean;
  /** Whether a feature id is live in this release. */
  isEnabled: (key: string | undefined) => boolean;
  /** Inverse of {@link FeatureGate.isEnabled}. */
  isLocked: (key: string | undefined) => boolean;
};

export const useFeatureGate = (): FeatureGate => {
  // Subscribed to EVERY reveal flag, not just `show_in_progress`: per-feature
  // reveals (the "777creatives" kind) live under their own storage keys, and a
  // snapshot of the blanket flag alone compares equal when one of those flips —
  // React would then skip the render and the code would look like it did
  // nothing until the page was reloaded.
  const snapshot = React.useSyncExternalStore(subscribeReveal, featureRevealSnapshot, () => '0:');
  // `revealed` is READ OUT of the snapshot (its leading flag is show_in_progress)
  // rather than fetched from storage again: same value the render was decided on,
  // and it makes the snapshot the memo's one true dependency — keying on a
  // separately-read boolean would hold the gate object stable when a PER-FEATURE
  // reveal flips, and consumers keyed on the gate identity would not recompute.
  return React.useMemo(
    () => ({
      revealed: snapshot.startsWith('1:'),
      isEnabled: isFeatureEnabled,
      isLocked: isFeatureLocked,
    }),
    [snapshot],
  );
};

export default useFeatureGate;
